import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { JobFair } from 'app/types';
import { KTSVG } from '_metronic/helpers';
import { Roles } from 'app/types';
import { useAuth } from 'app/context/auth-context';
import { usTimezones } from 'app/helpers/MiscUtils';
import { debounce } from 'lodash';
import { JobfairService } from 'app/services/JobfairService';
import { config } from '../../../../config';
const jobFairService = new JobfairService();

interface CreateEditJobFairFormProps {
    onSubmit: (values: any) => void;
    jobFair?: JobFair;
    jobfairPaymentsEnabled?: boolean;
    billingConfigLoaded?: boolean;

    orgName: string
}

const CreateEditJobFair: React.FC<CreateEditJobFairFormProps> = ({ onSubmit, jobFair, orgName, jobfairPaymentsEnabled = true, billingConfigLoaded = true }) => {
    const [error, setError] = useState<string | null>(null);
    const originalAlias = jobFair?.alias || '';
    const [aliasAvailable, setAliasAvailable] = useState<boolean | null>(null);
    const [checkingAlias, setCheckingAlias] = useState(false);


    const isEditMode = !!jobFair;
    const { user } = useAuth();

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    let isAdmin = false;
    if (user?.roles?.includes(Roles.Admin)) {
        isAdmin = true;
    }

    let validationStartDateSchema = Yup.date().required('Required');
    if (!isAdmin) {
        validationStartDateSchema = validationStartDateSchema.min(new Date(), 'Date must be in the future')
    }

    const handleSubmit = async (values: any) => {
        // Only block if alias has changed AND it's taken
        if (values.alias !== originalAlias && aliasAvailable === false) {
            setError('Alias is already taken. Please choose another one.');
            return;
        }
        setError(null);
        try {
            await onSubmit(values);
        } catch (err: any) {
            // Handle array of validation errors from backend
            if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
                const errorMessage = err.response.data.errors.join('\n');
                setError(errorMessage);
            } else {
                setError(err.message || 'An error occurred while saving the job fair');
            }
            formik.setSubmitting(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            name: jobFair?.name || orgName + ' Job Fair' || '',
            startDate: formatDateForInput(jobFair?.startDate || new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()),
            startTime: jobFair?.startTime || new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
            timezone: jobFair?.timezone || '',
            durationHours: jobFair?.durationHours || 3,
            durationMinutes: jobFair?.durationMinutes || 0,
            alias: jobFair?.alias || '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required').max(100, 'Must be 100 characters or less'),
            startDate: validationStartDateSchema,
            startTime: Yup.string().required('Required'),
            timezone: Yup.string().required('Required'),
            durationHours: Yup.number().min(0).required('Required').max(10, 'Must be 10 or less'),
            durationMinutes: Yup.number().min(0).max(59).required('Required'),
            alias: Yup.string()
                .required('Required')
                .min(3, 'Must be at least 3 characters')
                .max(30, 'Must be 30 characters or less')
                .matches(/^[a-zA-Z0-9-]+$/, 'Only alphanumeric characters and hyphens are allowed'),
        }),
        onSubmit: handleSubmit
    });

    useEffect(() => {
        const currentAlias = formik.values.alias;

        // Skip check if alias is empty or hasn't changed
        if (!currentAlias || currentAlias === originalAlias) {
            setAliasAvailable(true); // treat original alias as available
            return;
        }

        const checkAlias = debounce(async (alias: string) => {
            setCheckingAlias(true);
            try {
                const existing = await jobFairService.getJobFairByAlias(alias);
                setAliasAvailable(!existing); // true if free, false if taken
            } catch (err) {
                setAliasAvailable(null);
            } finally {
                setCheckingAlias(false);
            }
        }, 500);

        checkAlias(currentAlias);
        return () => checkAlias.cancel();
    }, [formik.values.alias, originalAlias]);

    return (
        <form onSubmit={formik.handleSubmit}>
            {error && (
                <div className="alert alert-danger mb-5">
                    <div className="d-flex flex-column">
                        <h4 className="mb-1 text-danger">Error</h4>
                        <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
                    </div>
                </div>
            )}

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Name</label>
                <input
                    type="text"
                    id="name"
                    {...formik.getFieldProps('name')}
                    className={`form-control form-control-solid mb-3 mb-lg-0 ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                        }`}
                />
                {formik.touched.name && formik.errors.name && (
                    <div className="text-danger mt-2">{formik.errors.name}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Start Date</label>
                <input
                    type="date"
                    id="startDate"
                    min={isAdmin ? '' : new Date().toISOString().split('T')[0]}
                    {...formik.getFieldProps('startDate')}
                    className={`form-control form-control-solid ${formik.touched.startDate && formik.errors.startDate ? 'is-invalid' : ''
                        }`}
                />
                {formik.touched.startDate && formik.errors.startDate && (
                    <div className="text-danger mt-2">{formik.errors.startDate}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Start Time</label>
                <div className='position-relative'>
                    <input
                        type="time"
                        id="startTime"
                        {...formik.getFieldProps('startTime')}
                        className={`form-control form-control-solid ${formik.touched.startTime && formik.errors.startTime ? 'is-invalid' : ''
                            }`}
                    />
                    <div className='position-absolute translate-middle-y top-50 end-0 me-4'>
                        <KTSVG path='/media/icons/duotone/Home/Alarm-clock.svg' className='svg-icon-2' />
                    </div>
                </div>
                {formik.touched.startTime && formik.errors.startTime && (
                    <div className="text-danger mt-2">{formik.errors.startTime}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Timezone</label>
                <select
                    id="timezone"
                    {...formik.getFieldProps('timezone')}
                    className='form-select form-select-solid'
                >
                    <option value="">Select Timezone</option>
                    {usTimezones.map((timezone) => (
                        <option key={timezone} value={timezone}>
                            {timezone}
                        </option>
                    ))}
                </select>
                {formik.touched.timezone && formik.errors.timezone && (
                    <div className="text-danger mt-2">{formik.errors.timezone}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Duration</label>
                <div className='d-flex'>
                    <div className='me-5'>
                        <input
                            type="number"
                            id="durationHours"
                            {...formik.getFieldProps('durationHours')}
                            className='form-control form-control-solid w-100px'
                            min="0"
                            max="10"
                        />
                        <span className='text-muted ms-2'>Hours</span>
                    </div>
                    <div>
                        <input
                            type="number"
                            id="durationMinutes"
                            {...formik.getFieldProps('durationMinutes')}
                            className='form-control form-control-solid w-100px'
                            min="0"
                            max="59"
                        />
                        <span className='text-muted ms-2'>Minutes</span>
                    </div>
                </div>
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Join URL</label>
                <div className="input-group mb-1">
                    <span className="input-group-text" id="alias-pretext">
                        {config.vjfpBaseUrl}join/
                    </span>
                    <input
                        type="text"
                        id="alias"
                        {...formik.getFieldProps('alias')}
                        placeholder="unique alias (e.g. CompanyName-JobFair-Date)"
                        className={`form-control ${formik.touched.alias && formik.errors.alias ? 'is-invalid' : ''}`}
                        aria-describedby="alias-pretext"
                    />
                </div>

                {/* Formik validation error */}
                {formik.touched.alias && formik.errors.alias && (
                    <div className="text-danger mt-2">{formik.errors.alias}</div>
                )}

                {/* Availability feedback */}
                {checkingAlias && <div className="text-muted mt-1">Checking availability...</div>}
                {aliasAvailable === true && !checkingAlias && formik.values.alias !== originalAlias && (
                    <div className="text-success mt-1">✅ Alias is available</div>
                )}
                {aliasAvailable === false && !checkingAlias && formik.values.alias !== originalAlias && (
                    <div className="text-danger mt-1">❌ Alias is already taken</div>
                )}
                {/* Original alias in update mode is always valid */}
                {formik.values.alias === originalAlias && !checkingAlias && (
                    <div className="text-muted mt-1">This is the current alias for this job fair.</div>
                )}
            </div>
            <div className='text-center pt-15'>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formik.isSubmitting || !billingConfigLoaded}
                >
                    {formik.isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Processing...
                        </>
                    ) : (!billingConfigLoaded ? 'Loading...' : isEditMode ? 'Update' : jobfairPaymentsEnabled ? 'Create & Pay' : 'Create')}
                </button>
            </div>
        </form>
    );
};

export default CreateEditJobFair;
